import { useEffect, useState } from "react";
import {
    Award, Check, Download, Edit, FileSpreadsheet, ListChecks, Loader2, Play, Plus, Trash, Upload, X,
} from "lucide-react";
import { DatePicker, Select } from "antd";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";

import { Reactable } from "../../components/ui/reactable";
import { Button } from "../../components/ui/button";
import { ConfirmPopupModal, DialogModal } from "../../components/ui/dialogModal";
import { Input, Textarea } from "../../components/ui/input";
import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { checkUserPermissionFunc, getCSVData, truncateText } from "../../components/common/CommonFunctions";
import { PERK_CRITERIA, PERK_EVALUATE, PERK_SHEET, PERK_VOCABULARY } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";
import { modalDescriptionFieldProps, modalFieldLabelClass, modalInputClass, modalTextareaClass } from "../../components/ui/fieldStyles";

const STATUS_CHIP = {
    draft: "bg-gray-100 text-gray-700 dark:bg-gray-700/40 dark:text-gray-300",
    approved: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
    evaluated: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
    closed: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
};

const EMPTY_RULE = { metric: "", operator: "gte", value: "", value_to: "", points: "", is_required: false };

const EMPTY_FORM = {
    id: null,
    name: "",
    perk_type: "bonus",
    from_date: null,
    to_date: null,
    match_mode: "points",
    min_points: "",
    notes: "",
};

const PerkCriteria = () => {
    const { fetch, create, update } = useApiMethods();
    const navigate = useNavigate();

    const [rows, setRows] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [serverTrigger, setServerTrigger] = useState(false);

    // The rule vocabulary comes from the server so the editor can never offer a
    // metric the evaluation engine cannot compute.
    const [vocabulary, setVocabulary] = useState({ metrics: [], operators: [], match_modes: [], perk_types: [] });

    const [openDialog, setOpenDialog] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState(EMPTY_FORM);
    const [rules, setRules] = useState([{ ...EMPTY_RULE }]);

    const [selectedRow, setSelectedRow] = useState(null);
    const [confirm, setConfirm] = useState(null); // 'approve' | 'evaluate' | 'delete'

    // Sheet flow. Uploading only parses — the criteria are created through the
    // same endpoint the editor uses, so a sheet cannot bypass its validation.
    const [sheetOpen, setSheetOpen] = useState(false);
    const [sheetBusy, setSheetBusy] = useState(false);
    const [sheetResult, setSheetResult] = useState(null);

    const { add: addPermission, edit: editPermission, draft: draftPermission } =
        checkUserPermissionFunc("payroll", "Perk Criteria", "SubMenu");

    useEffect(() => {
        let cancelled = false;
        const controller = new AbortController();
        (async () => {
            const response = await fetch(PERK_VOCABULARY, {}, undefined, controller.signal);
            if (cancelled || response?.status === "cancelled") return;
            if (response?.status === "success") setVocabulary(response.data);
        })();
        return () => {
            cancelled = true;
            controller.abort();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const metricOptions = vocabulary.metrics.map((m) => ({ value: m.value, label: m.label }));
    const operatorOptions = vocabulary.operators.map((o) => ({ value: o.value, label: o.label }));
    const metricMeta = (value) => vocabulary.metrics.find((m) => m.value === value);

    const isPoints = formData.match_mode === "points";
    const totalPoints = rules.reduce((sum, r) => sum + (Number(r.points) || 0), 0);

    const resetForm = () => {
        setFormData(EMPTY_FORM);
        setRules([{ ...EMPTY_RULE }]);
        setEditMode(false);
        setOpenDialog(false);
        setSelectedRow(null);
    };

    const setField = (field, value) => setFormData((prev) => ({ ...prev, [field]: value }));
    const setRuleField = (index, field, value) =>
        setRules((prev) => prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)));
    const addRule = () => setRules((prev) => [...prev, { ...EMPTY_RULE }]);
    const removeRule = (index) => setRules((prev) => prev.filter((_, i) => i !== index));

    const openForEdit = async (row) => {
        setLoading(true);
        const response = await fetch(PERK_CRITERIA, { id: row.id });
        setLoading(false);
        if (response?.status !== "success") {
            ToastMsg("error", response?.message || "Could not load the criteria");
            return;
        }
        const detail = response.data;
        setEditMode(true);
        setSelectedRow(row);
        setFormData({
            id: detail.id,
            name: detail.name || "",
            perk_type: detail.perk_type || "bonus",
            from_date: detail.from_date ? dayjs(detail.from_date) : null,
            to_date: detail.to_date ? dayjs(detail.to_date) : null,
            match_mode: detail.match_mode || "points",
            min_points: detail.min_points ?? "",
            notes: detail.notes || "",
        });
        setRules(
            (detail.rules || []).length
                ? detail.rules.map((r) => ({
                      metric: r.metric,
                      operator: r.operator,
                      value: r.value ?? "",
                      value_to: r.value_to ?? "",
                      points: r.points ?? "",
                      is_required: !!r.is_required,
                  }))
                : [{ ...EMPTY_RULE }],
        );
        setOpenDialog(true);
    };

    const payload = (isDraft) => ({
        ...(formData.id ? { id: formData.id } : {}),
        name: formData.name,
        perk_type: formData.perk_type,
        from_date: formData.from_date ? dayjs(formData.from_date).format("YYYY-MM-DD") : null,
        to_date: formData.to_date ? dayjs(formData.to_date).format("YYYY-MM-DD") : null,
        match_mode: formData.match_mode,
        min_points: formData.min_points === "" ? 0 : Number(formData.min_points),
        notes: formData.notes,
        is_draft: isDraft,
        rules: rules
            .filter((r) => r.metric)
            .map((r, index) => ({
                metric: r.metric,
                operator: r.operator,
                value: r.value === "" ? 0 : Number(r.value),
                value_to: r.value_to === "" ? null : Number(r.value_to),
                points: r.points === "" ? 0 : Number(r.points),
                is_required: !!r.is_required,
                sort_order: index,
            })),
    });

    const save = async (isDraft) => {
        setLoading(true);
        const body = payload(isDraft);
        const response = editMode ? await update(PERK_CRITERIA, body) : await create(PERK_CRITERIA, body);
        setLoading(false);
        if (response?.status === "success") {
            ToastMsg("success", response.message || "Criteria saved");
            resetForm();
            setServerTrigger((prev) => !prev);
        } else {
            // The server returns per-field messages; the first one is more use
            // than the generic summary.
            const fieldError = response?.errors && Object.values(response.errors)[0];
            ToastMsg("error", fieldError || response?.message || "Failed to save the criteria");
        }
    };

    const runConfirm = async () => {
        if (!selectedRow) return;
        setLoading(true);
        let response;
        if (confirm === "evaluate") {
            response = await create(PERK_EVALUATE, { id: selectedRow.id });
        } else if (confirm === "approve") {
            response = await update(PERK_CRITERIA, { id: selectedRow.id, approve: true });
        } else {
            response = await update(PERK_CRITERIA, { id: selectedRow.id, draft_delete: true });
        }
        setLoading(false);
        if (response?.status === "success") {
            ToastMsg("success", response.message);
            setServerTrigger((prev) => !prev);
            if (confirm === "evaluate") navigate(`/payroll/perkcriteria/${selectedRow.id}/review`);
        } else {
            const fieldError = response?.errors && Object.values(response.errors)[0];
            ToastMsg("error", fieldError || response?.message || "Action failed");
        }
        setConfirm(null);
        setSelectedRow(null);
    };

    const downloadTemplate = async () => {
        setSheetBusy(true);
        try {
            await getCSVData(PERK_SHEET, {}, "csv");
        } catch (error) {
            ToastMsg("error", error?.message || "Could not download the template");
        }
        setSheetBusy(false);
    };

    const uploadSheet = async (file) => {
        if (!file) return;
        setSheetBusy(true);
        setSheetResult(null);
        const form = new FormData();
        form.append("file", file);
        const response = await create(PERK_SHEET, form, true);
        setSheetBusy(false);
        if (response?.status === "success") {
            setSheetResult(response.data);
            ToastMsg(response.data?.problems?.length ? "warning" : "success", response.message);
        } else {
            ToastMsg("error", response?.message || "Could not read the sheet");
            setSheetResult({ problems: response?.data?.problems || [], can_create: false });
        }
    };

    const createFromSheet = async () => {
        if (!sheetResult?.criteria) return;
        setSheetBusy(true);
        const response = await create(PERK_CRITERIA, { ...sheetResult.criteria, is_draft: false });
        setSheetBusy(false);
        if (response?.status === "success") {
            ToastMsg("success", response.message);
            setSheetOpen(false);
            setSheetResult(null);
            setServerTrigger((prev) => !prev);
        } else {
            const fieldError = response?.errors && Object.values(response.errors)[0];
            ToastMsg("error", fieldError || response?.message || "Could not create the criteria");
        }
    };

    const columns = [
        {
            accessor: "name",
            Header: "Criteria",
            sortable: true,
            filterable: true,
            Cell: ({ value }) => truncateText(value, 30, "300px", "balance", "-"),
        },
        {
            accessor: "perk_type",
            Header: "For",
            filterable: true,
            Cell: ({ value }) => <span className="capitalize">{value || "-"}</span>,
        },
        {
            accessor: "from_date",
            Header: "Measured over",
            sortable: true,
            Cell: ({ row }) => (
                <span className="whitespace-nowrap tabular-nums">
                    {row.from_date ? dayjs(row.from_date).format("DD-MM-YYYY") : "-"}
                    {" to "}
                    {row.to_date ? dayjs(row.to_date).format("DD-MM-YYYY") : "-"}
                </span>
            ),
        },
        {
            accessor: "match_mode",
            Header: "How",
            Cell: ({ row }) => (
                <span className="whitespace-nowrap">
                    {row.match_mode === "points"
                        ? `${row.min_points} points to qualify`
                        : row.match_mode === "all"
                        ? "All conditions"
                        : "Any condition"}
                </span>
            ),
        },
        {
            accessor: "rule_count",
            Header: "Conditions",
            Cell: ({ value }) => <span className="tabular-nums">{value ?? 0}</span>,
        },
        {
            accessor: "candidate_count",
            Header: "Qualified",
            Cell: ({ row }) =>
                row.status === "evaluated" ? (
                    <span className="tabular-nums font-medium">{row.candidate_count ?? 0}</span>
                ) : (
                    <span className="text-muted-foreground">-</span>
                ),
        },
        {
            accessor: "status",
            Header: "Status",
            filterable: true,
            Cell: ({ value }) => (
                <span
                    className={cn(
                        "px-2 py-1 text-xs font-medium rounded-full capitalize",
                        STATUS_CHIP[value] || STATUS_CHIP.draft,
                    )}
                >
                    {value || "draft"}
                </span>
            ),
        },
        {
            accessor: "action",
            Header: "Actions",
            sortable: false,
            Cell: ({ row }) => {
                const isFrozen = row.status === "evaluated" || row.status === "closed";
                return (
                    <div className="flex items-center gap-1">
                        {isFrozen && (
                            <Button
                                variant="ghost"
                                size="sm"
                                title="Review shortlist"
                                className="text-sidebar-primary hover:bg-blue-50 dark:hover:bg-gray-700"
                                onClick={() => navigate(`/payroll/perkcriteria/${row.id}/review`)}
                            >
                                <ListChecks className="h-4 w-4" />
                            </Button>
                        )}
                        {editPermission && !isFrozen && (
                            <Button
                                variant="ghost"
                                size="sm"
                                title="Edit"
                                className="text-sidebar-primary hover:bg-blue-50 dark:hover:bg-gray-700"
                                onClick={() => openForEdit(row)}
                            >
                                <Edit className="h-4 w-4" />
                            </Button>
                        )}
                        {editPermission && (row.status === "draft" || !row.status) && (
                            <Button
                                variant="ghost"
                                size="sm"
                                title="Approve"
                                className="text-sidebar-primary hover:bg-sidebar-accent dark:hover:bg-gray-700"
                                onClick={() => {
                                    setSelectedRow(row);
                                    setConfirm("approve");
                                }}
                            >
                                <Check className="h-4 w-4" />
                            </Button>
                        )}
                        {editPermission && row.status === "approved" && (
                            <Button
                                variant="ghost"
                                size="sm"
                                title="Evaluate"
                                className="text-emerald-600 hover:bg-emerald-50 dark:hover:bg-gray-700"
                                onClick={() => {
                                    setSelectedRow(row);
                                    setConfirm("evaluate");
                                }}
                            >
                                <Play className="h-4 w-4" />
                            </Button>
                        )}
                        {row.is_draft && (
                            <Button
                                variant="ghost"
                                size="sm"
                                title="Delete draft"
                                className="text-red-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/30"
                                onClick={() => {
                                    setSelectedRow(row);
                                    setConfirm("delete");
                                }}
                            >
                                <Trash className="h-4 w-4" />
                            </Button>
                        )}
                    </div>
                );
            },
        },
    ];

    const confirmCopy = {
        approve: {
            title: "Approve these criteria?",
            description: "Approved criteria can be evaluated. Nobody is paid anything at this point.",
            label: "Approve",
            type: "info",
        },
        evaluate: {
            title: "Evaluate and build the shortlist?",
            description:
                "Every employee in the period is scored and the shortlist is stored for review. Re-running later refreshes the scores but keeps any include/exclude decisions already recorded.",
            label: "Evaluate",
            type: "warning",
        },
        delete: {
            title: "Delete this draft?",
            description: "The draft criteria and their conditions will be removed.",
            label: "Delete",
            type: "danger",
        },
    }[confirm] || {};

    return (
        <ContainerCard>
            <ModuleTitleCard
                Icon={Award}
                title="Perk Criteria"
                subtitle="Score employees for bonus, incentive and increment on output as well as attendance"
                totalRecords={totalRecords}
                buttonEnable={!!addPermission}
                setOpenDialog={setOpenDialog}
                buttonName="Add Criteria"
            />

            {/* Sheet flow, offered alongside the on-screen editor: HR often
                authors these with management in Excel and circulates them for
                sign-off before anything is entered. */}
            {addPermission && (
                <div className="flex flex-wrap items-center gap-2 px-3 pb-2">
                    <Button variant="outline" size="sm" onClick={() => setSheetOpen(true)}>
                        <FileSpreadsheet className="h-4 w-4 mr-1" />
                        Use a criteria sheet
                    </Button>
                    <span className="text-[11px] text-muted-foreground">
                        Download the template, fill it in, upload it back
                    </span>
                </div>
            )}

            <Reactable
                pagename="perk-criteria"
                columns={columns}
                data={rows}
                setData={setRows}
                setTotalCount={setTotalRecords}
                url={PERK_CRITERIA}
                serverSide={true}
                serverTrigger={serverTrigger}
                rowSize="sm"
                isCSVRequired={true}
                isPDFRequired={true}
                isDraftRequired={!!draftPermission}
                isDateRequired={false}
            />

            <DialogModal
                open={openDialog}
                onClose={resetForm}
                onSave={() => save(false)}
                onDraft={draftPermission ? () => save(true) : undefined}
                title={editMode ? "Edit Criteria" : "Add Criteria"}
                closeLabel="Cancel"
                saveLabel={editMode ? "Update" : "Save"}
                draftLabel="Draft"
                editMode={editMode}
                loading={loading}
                width="900px"
            >
                <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                        <div className="space-y-2 sm:col-span-2">
                            <label htmlFor="name" className={modalFieldLabelClass}>
                                Name <span className="text-red-500">*</span>
                            </label>
                            <Input
                                id="name"
                                value={formData.name}
                                onChange={(e) => setField("name", e.target.value)}
                                placeholder="Q2 performance bonus"
                                disabled={loading}
                            />
                        </div>
                        <div className="space-y-2">
                            <label className={modalFieldLabelClass}>For</label>
                            <Select
                                className="w-full"
                                size="large"
                                value={formData.perk_type}
                                options={vocabulary.perk_types.map((p) => ({
                                    value: p.value,
                                    label: p.value.charAt(0).toUpperCase() + p.value.slice(1),
                                }))}
                                onChange={(value) => setField("perk_type", value)}
                                disabled={loading}
                            />
                        </div>
                        <div className="space-y-2">
                            <label className={modalFieldLabelClass}>
                                Measured over <span className="text-red-500">*</span>
                            </label>
                            <DatePicker.RangePicker
                                className="w-full"
                                size="large"
                                format="DD-MM-YYYY"
                                value={[formData.from_date, formData.to_date]}
                                onChange={(dates) => {
                                    setField("from_date", dates ? dates[0] : null);
                                    setField("to_date", dates ? dates[1] : null);
                                }}
                                disabled={loading}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <label className={modalFieldLabelClass}>
                                How the conditions combine
                            </label>
                            <Select
                                className="w-full"
                                size="large"
                                value={formData.match_mode}
                                options={vocabulary.match_modes}
                                onChange={(value) => setField("match_mode", value)}
                                disabled={loading}
                            />
                            {/* The reason this feature exists, said out loud. */}
                            <p className="text-[11px] text-muted-foreground">
                                {isPoints
                                    ? "Each condition met adds its weight. Give output a high weight and punctuality a low one, and a hard worker who is often late still qualifies."
                                    : formData.match_mode === "all"
                                    ? "Every condition must be met. A single attendance threshold will exclude a hard worker who is often late."
                                    : "At least one condition must be met."}
                            </p>
                        </div>
                        {isPoints && (
                            <div className="space-y-2">
                                <label htmlFor="min_points" className={modalFieldLabelClass}>
                                    Points needed to qualify <span className="text-red-500">*</span>
                                </label>
                                <Input
                                    id="min_points"
                                    type="number"
                                    min="0"
                                    step="1"
                                    value={formData.min_points}
                                    onChange={(e) => setField("min_points", e.target.value)}
                                    placeholder="60"
                                    disabled={loading}
                                />
                                <p
                                    className={cn(
                                        "text-[11px]",
                                        Number(formData.min_points) > totalPoints
                                            ? "text-red-600 dark:text-red-400"
                                            : "text-muted-foreground",
                                    )}
                                >
                                    Conditions below add up to {totalPoints} points
                                    {Number(formData.min_points) > totalPoints
                                        ? " — this threshold can never be reached"
                                        : ""}
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Rule editor */}
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <label className={modalFieldLabelClass}>
                                Conditions <span className="text-red-500">*</span>
                            </label>
                            <Button type="button" variant="outline" size="sm" onClick={addRule} disabled={loading}>
                                <Plus className="h-3.5 w-3.5 mr-1" />
                                Add condition
                            </Button>
                        </div>

                        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                            <table className="min-w-full text-sm">
                                <thead className="bg-gray-50 dark:bg-gray-800">
                                    <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                                        <th className="px-3 py-2 font-medium min-w-[190px]">Measure</th>
                                        <th className="px-3 py-2 font-medium min-w-[130px]">Comparison</th>
                                        <th className="px-3 py-2 font-medium">Value</th>
                                        {rules.some((r) => r.operator === "between") && (
                                            <th className="px-3 py-2 font-medium">Upper</th>
                                        )}
                                        {isPoints && <th className="px-3 py-2 font-medium">Weight</th>}
                                        <th className="px-3 py-2 font-medium">Must pass</th>
                                        <th className="px-3 py-2 font-medium w-10"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rules.map((rule, index) => {
                                        const meta = metricMeta(rule.metric);
                                        return (
                                            <tr key={index} className="border-t border-gray-200 dark:border-gray-700">
                                                <td className="px-2 py-1.5">
                                                    <Select
                                                        className="w-full"
                                                        size="large"
                                                        value={rule.metric || undefined}
                                                        options={metricOptions}
                                                        placeholder="Choose a measure"
                                                        onChange={(value) => setRuleField(index, "metric", value)}
                                                        disabled={loading}
                                                        showSearch
                                                        optionFilterProp="label"
                                                    />
                                                    {meta && (
                                                        <span className="text-[10px] text-muted-foreground">
                                                            {meta.higher_is_better ? "higher is better" : "lower is better"}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-2 py-1.5">
                                                    <Select
                                                        className="w-full"
                                                        size="large"
                                                        value={rule.operator}
                                                        options={operatorOptions}
                                                        onChange={(value) => setRuleField(index, "operator", value)}
                                                        disabled={loading}
                                                    />
                                                </td>
                                                <td className="px-2 py-1.5">
                                                    <Input
                                                        type="number"
                                                        step="0.01"
                                                        value={rule.value}
                                                        onChange={(e) => setRuleField(index, "value", e.target.value)}
                                                        disabled={loading}
                                                    />
                                                </td>
                                                {rules.some((r) => r.operator === "between") && (
                                                    <td className="px-2 py-1.5">
                                                        <Input
                                                            type="number"
                                                            step="0.01"
                                                            value={rule.value_to}
                                                            onChange={(e) => setRuleField(index, "value_to", e.target.value)}
                                                            disabled={loading || rule.operator !== "between"}
                                                        />
                                                    </td>
                                                )}
                                                {isPoints && (
                                                    <td className="px-2 py-1.5">
                                                        <Input
                                                            type="number"
                                                            min="0"
                                                            step="1"
                                                            value={rule.points}
                                                            onChange={(e) => setRuleField(index, "points", e.target.value)}
                                                            placeholder="0"
                                                            disabled={loading}
                                                        />
                                                    </td>
                                                )}
                                                <td className="px-2 py-1.5 text-center">
                                                    {/* A veto in every mode, so a hard gate can coexist
                                                        with a weighted score instead of being outvoted. */}
                                                    <input
                                                        type="checkbox"
                                                        checked={rule.is_required}
                                                        onChange={(e) => setRuleField(index, "is_required", e.target.checked)}
                                                        disabled={loading}
                                                        className="h-4 w-4 accent-current text-sidebar-primary"
                                                        aria-label="Must pass regardless of points"
                                                    />
                                                </td>
                                                <td className="px-2 py-1.5 text-center">
                                                    {rules.length > 1 && (
                                                        <Button
                                                            type="button"
                                                            variant="ghost"
                                                            size="sm"
                                                            title="Remove condition"
                                                            className="text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30"
                                                            onClick={() => removeRule(index)}
                                                            disabled={loading}
                                                        >
                                                            <X className="h-4 w-4" />
                                                        </Button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        <p className="text-[11px] text-muted-foreground">
                            A condition marked <b>Must pass</b> is required whatever the points say — use it for hard
                            gates, not for attendance.
                        </p>
                    </div>

                    <div className="space-y-2">
                        <label htmlFor="notes" className={modalFieldLabelClass}>
                            Notes
                        </label>
                        <Textarea
                            id="notes"
                            rows={2}
                            value={formData.notes}
                            onChange={(e) => setField("notes", e.target.value)}
                            disabled={loading}
                        />
                    </div>
                </form>
            </DialogModal>

            <DialogModal
                open={sheetOpen}
                onClose={() => {
                    setSheetOpen(false);
                    setSheetResult(null);
                }}
                onSave={createFromSheet}
                title="Criteria sheet"
                closeLabel="Close"
                saveLabel="Create criteria"
                hideSaveButton={!sheetResult?.can_create}
                loading={sheetBusy}
                width="680px"
            >
                <div className="space-y-4">
                    <ol className="space-y-3 text-sm">
                        <li className="flex flex-wrap items-center gap-3">
                            <span className="font-medium text-gray-900 dark:text-white">1.</span>
                            <Button variant="outline" size="sm" onClick={downloadTemplate} disabled={sheetBusy}>
                                <Download className="h-4 w-4 mr-1" />
                                Download the template
                            </Button>
                            <span className="text-muted-foreground text-xs">
                                Lists every measure the system can score on
                            </span>
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="font-medium text-gray-900 dark:text-white">2.</span>
                            <span className="text-muted-foreground text-xs pt-1">
                                Fill in the header and one row per condition. Give output a high weight and
                                punctuality a low one, so a hard worker who is often late still qualifies.
                            </span>
                        </li>
                        <li className="flex flex-wrap items-center gap-3">
                            <span className="font-medium text-gray-900 dark:text-white">3.</span>
                            <label
                                className={cn(
                                    "inline-flex items-center gap-1 rounded-md border px-3 h-9 text-sm cursor-pointer",
                                    "border-input bg-background hover:bg-accent hover:text-accent-foreground",
                                    sheetBusy && "pointer-events-none opacity-50",
                                )}
                            >
                                <Upload className="h-4 w-4" />
                                Upload the filled sheet
                                <input
                                    type="file"
                                    accept=".csv,text/csv"
                                    className="hidden"
                                    onChange={(e) => uploadSheet(e.target.files?.[0])}
                                />
                            </label>
                            <span className="text-muted-foreground text-xs">Nothing is saved until you confirm</span>
                        </li>
                    </ol>

                    {sheetBusy && (
                        <p className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Working
                        </p>
                    )}

                    {sheetResult?.problems?.length > 0 && (
                        <div className="rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3">
                            <p className="text-xs font-medium text-amber-800 dark:text-amber-300">
                                Fix these in the sheet and upload again
                            </p>
                            <ul className="mt-1 space-y-0.5 text-xs text-amber-800/90 dark:text-amber-300/90 list-disc pl-4">
                                {sheetResult.problems.map((problem, i) => (
                                    <li key={i}>{problem}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {sheetResult?.criteria?.rules?.length > 0 && (
                        <div className="space-y-2">
                            <p className="text-xs uppercase tracking-wide text-muted-foreground">
                                {sheetResult.criteria.name || "Untitled"} ·{" "}
                                {sheetResult.criteria.from_date} to {sheetResult.criteria.to_date} ·{" "}
                                {sheetResult.criteria.match_mode === "points"
                                    ? `${sheetResult.criteria.min_points} points to qualify`
                                    : sheetResult.criteria.match_mode}
                            </p>
                            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                                <table className="min-w-full text-sm">
                                    <thead className="bg-gray-50 dark:bg-gray-800">
                                        <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                                            <th className="px-3 py-2 font-medium">Condition</th>
                                            <th className="px-3 py-2 font-medium text-right">Weight</th>
                                            <th className="px-3 py-2 font-medium">Must pass</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sheetResult.criteria.rules.map((rule, i) => (
                                            <tr key={i} className="border-t border-gray-200 dark:border-gray-700">
                                                <td className="px-3 py-1.5">
                                                    {metricMeta(rule.metric)?.label || rule.metric}{" "}
                                                    <span className="text-muted-foreground">
                                                        {vocabulary.operators.find((o) => o.value === rule.operator)?.label ||
                                                            rule.operator}{" "}
                                                        {rule.value}
                                                    </span>
                                                </td>
                                                <td className="px-3 py-1.5 text-right tabular-nums">{rule.points}</td>
                                                <td className="px-3 py-1.5">{rule.is_required ? "yes" : ""}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            </DialogModal>

            <ConfirmPopupModal
                open={!!confirm}
                onClose={() => {
                    setConfirm(null);
                    setSelectedRow(null);
                }}
                onConfirm={runConfirm}
                title={confirmCopy.title}
                description={confirmCopy.description}
                confirmLabel={confirmCopy.label}
                type={confirmCopy.type}
                destructive={confirm === "delete"}
                loading={loading}
            />
        </ContainerCard>
    );
};

export default PerkCriteria;
